import { useEffect, useRef, useState } from "react";

import { Button, Callout, Colors, OverlayToaster } from "@blueprintjs/core";
import { addDays, isSameDay } from "date-fns";

import ongoing from "@/../../hypetrain/static/img/for-coaches/c-ongoing.svg";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { ActionItemDisplay } from "@/components/pages_logged_in/program/components/set-components/ActionItemDisplay";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { CARD_CLASSES } from "@/css/constants";
import {
  ProgramSurveyQuery,
  useProgramSurveyQueryLazyQuery,
  useSendNudgeSurveyMutation,
} from "@/graphql";
import {
  CoachParticipant,
  ComponentParticipant,
  MainComponent,
  ProgramParticipant,
} from "@/types/programs";
import { ensureNonNullArray, formatErrors } from "@/utils/array";
import { formatDate } from "@/utils/datetime";

type SurveryQuerySurvey = NonNullable<ProgramSurveyQuery["programSurvey"]>;

function SurveyPreview({
  programOwnerName,
  component,
}: {
  programOwnerName: string;
  component: MainComponent;
}) {
  return (
    <Box
      css={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #E2E2E2",
        borderRadius: 10,
        padding: "36px 24px",
        marginTop: 16,
      }}
    >
      <EmbedSvg src={ongoing} />
      <Box
        mt={4}
        css={{
          fontSize: 24,
          color: "#394B59",
          fontWeight: "bold",
        }}
      >
        Survey Reminder
      </Box>
      <Box mt={2} textAlign="center">
        {programOwnerName} flagged "{component?.name}" as high priority and is
        requesting your participation. Please take a moment to complete the
        survey now.
      </Box>
      <Button large={true} style={{ marginTop: 24 }} disabled>
        Take Survey
      </Button>
    </Box>
  );
}

function hasListElement(markdownText: string) {
  // Regular expression to check for unordered list items
  const unorderedListRegex = /^(\s*[-*+]\s)/gm;

  // Regular expression to check for ordered list items
  const orderedListRegex = /^(\s*\d+\.\s)/gm;

  return (
    unorderedListRegex.test(markdownText) || orderedListRegex.test(markdownText)
  );
}

interface ActionItemPreviewProps {
  component: MainComponent;
  survey?: SurveryQuerySurvey | null;
}

function ActionItemPreview({ component, survey }: ActionItemPreviewProps) {
  const { targetDueDate } = component;
  const dueDate = formatDate(targetDueDate, "MMM d");
  const formFields = ensureNonNullArray(survey?.formFields);
  const metaData = buildComponentMetadata({ component });
  const isVideoOrArticle = metaData.isVideo || metaData.isArticle;

  const isTomorrow = targetDueDate
    ? isSameDay(new Date(targetDueDate), addDays(new Date(), 1))
    : false;

  const buttonText =
    metaData.isAIRoleplay || isVideoOrArticle
      ? "View Program"
      : "Go To Dashboard";

  const hasList = hasListElement(component?.participantGuidance || "");

  return (
    <Box
      css={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #E2E2E2",
        borderRadius: 10,
        padding: "36px 24px",
        marginTop: 16,
      }}
    >
      {dueDate && (
        <Flex
          css={{
            fontSize: 12,
            color: "#725C01",
            backgroundColor: "#FFFDF2",
            alignItems: "center",
            borderRadius: 4,
            padding: "4px 32px",
          }}
        >
          <Box
            css={{
              borderRadius: "50%",
              backgroundColor: "#FAE89D",
              width: 10,
              height: 10,
              marginRight: 5,
            }}
          ></Box>
          <Box>Due {isTomorrow ? "Tomorrow" : dueDate}</Box>
        </Flex>
      )}
      <Box
        mb={2}
        css={{
          fontSize: 32,
          color: "#394B59",
          fontWeight: "bold",
          textAlign: "center",
        }}
      >
        {component?.name}
      </Box>
      {component?.participantGuidance && (
        <Box mt={2} textAlign={hasList ? "left" : "left"}>
          <RichContentRenderer content={component?.participantGuidance} />
        </Box>
      )}
      {formFields.length > 0 && (
        <Box
          flex={1}
          p={0}
          mt={24}
          className={CARD_CLASSES}
          css={{ background: Colors.WHITE }}
          width={"100%"}
        >
          {formFields.map((question, index) => {
            const isLastItem = index === formFields.length - 1;
            return (
              <Box
                key={question?.id}
                css={{
                  borderBottom: isLastItem ? undefined : "solid 0.5px #E2E2E2",
                  padding: "16px 22px",
                }}
              >
                <ActionItemDisplay
                  isLead={false}
                  isComplete={false}
                  question={question}
                  componentStatus={component.status}
                  hasLinkIcon={false}
                  isVideoOrArticle={isVideoOrArticle}
                />
              </Box>
            );
          })}
        </Box>
      )}
      <Button large={true} style={{ marginTop: 24 }} disabled>
        {buttonText}
      </Button>
    </Box>
  );
}

interface SendNudgeDialogProps {
  open: boolean;
  onClose: () => void;
  participant: ComponentParticipant | ProgramParticipant | CoachParticipant;
  component: MainComponent;
  onFinished: () => void;
}

export function SendNudgeDialog({
  open,
  onClose,
  participant,
  component,
  onFinished,
}: SendNudgeDialogProps) {
  const [result, setResult] = useState<{
    success: boolean;
    errorMsg: string;
  } | null>(null);

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const currentUser = getUser();
  const programOwnerName = currentUser?.name || currentUser?.email || "";
  const toasterRef = useRef<OverlayToaster>(null);

  const [fetch, { data: surveyData }] = useProgramSurveyQueryLazyQuery();

  useEffect(() => {
    if (component?.survey?.id) {
      fetch({ variables: { programSurveyId: component.survey?.id } });
    }
  }, [component?.survey?.id]);

  const onError = (error: Error) => {
    setResult({
      success: false,
      errorMsg:
        error.message || "Failed to send nudge. Please try again later.",
    });
  };

  const [sendNudgeSurvey, { loading: sending }] = useSendNudgeSurveyMutation({
    onCompleted: ({ sendNudgeSurvey }) => {
      if (sendNudgeSurvey?.ok) {
        toasterRef.current?.show({
          message: "Reminder email sent successfully",
          intent: "success",
        });
        setResult(null);
        onFinished();
      } else {
        const errorMsg = formatErrors(sendNudgeSurvey?.errors);
        onError(new Error(errorMsg));
      }
    },
    onError,
  });

  const metaData = buildComponentMetadata({ component });
  let subject = `⏰ Reminder: ${component?.name} participation requested by ${programOwnerName}`;
  if (metaData.isActionItems) {
    const isTomorrow = component?.targetDueDate
      ? isSameDay(new Date(component?.targetDueDate), addDays(new Date(), 1))
      : false;
    const dueDate = formatDate(component?.targetDueDate, "MM/dd/yyyy");
    subject = `⏰ Reminder: ${component?.name} due ${
      isTomorrow ? "tomorrow" : "on " + dueDate
    }`;
  }

  const { user } = participant;

  if (!user) return null;

  const disabledSubmit = !!component?.prerequisiteComponent;

  return (
    <>
      <OverlayToaster position="top" ref={toasterRef} usePortal={true} />
      <StyledDialog
        isOpen={open}
        lazy={true}
        title={<Box fontSize={18}>Send Nudge</Box>}
        width="570px"
        onClose={handleClose}
        onOpening={() => {
          setResult(null);
        }}
      >
        <Box css={{ padding: "12px 12px 12px 24px" }}>
          <Box>
            <Box css={titleCss}>Recipient</Box>
            <Box css={{ display: "flex", alignItems: "center", gap: 4 }} mt={2}>
              <Tooltip content={user.email}>
                <Avatar user={user} size={20} />
              </Tooltip>
              <Box>{user.name || user.email || ""}</Box>
            </Box>
          </Box>
          <Box mt={3}>
            <Box css={titleCss}>Subject</Box>
            <Box mt={2}>{subject}</Box>
          </Box>
          <Box mt={3}>
            <Box css={titleCss}>Preview</Box>
            {metaData.isSurvey && (
              <SurveyPreview
                programOwnerName={programOwnerName}
                component={component}
              />
            )}
            {(metaData.isActionItems ||
              metaData.isAIRoleplay ||
              metaData.isVideo ||
              metaData.isArticle) && (
              <ActionItemPreview
                component={component}
                survey={surveyData?.programSurvey}
              />
            )}
          </Box>
          {result && !result?.success && (
            <Callout icon={null} intent="danger" style={{ marginTop: 8 }}>
              {result?.errorMsg}
            </Callout>
          )}
          {disabledSubmit && (
            <Callout icon={null} intent="warning" style={{ marginTop: 8 }}>
              Cannot send nudge when participant is not complete{" "}
              {component?.prerequisiteComponent?.name}
            </Callout>
          )}
          <Flex mt={3} justifyContent="space-between">
            <Button
              large={true}
              intent="primary"
              onClick={() => {
                sendNudgeSurvey({
                  variables: {
                    componentId: component.id,
                    userId: user?.id || "",
                  },
                });
              }}
              loading={sending}
              disabled={disabledSubmit}
            >
              Send Nudge
            </Button>
          </Flex>
        </Box>
      </StyledDialog>
    </>
  );
}

const titleCss = {
  fontSize: "16px",
  fontWeight: 500,
};
